import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Users, AlertCircle, UserPlus, Shield, ShieldOff, Trash2, LogOut } from 'lucide-react';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import InviteModal from '../components/team/InviteModal';
import ConfirmRemoveModal from '../components/team/ConfirmRemoveModal';

const TeamPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();

  const [members, setMembers] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // memberId in progress
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchData = async () => {
    try {
      const [membersRes, companyRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies/me/members`),
        axios.get(`${API_URL}/api/companies/me`),
      ]);
      const sorted = [...membersRes.data].sort((a, b) => {
        if (a.role !== b.role) return a.role === 'admin' ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '', language);
      });
      setMembers(sorted);
      setCompanyName(companyRes.data?.name || '');
      setError('');
    } catch (err) {
      logger.error('Failed to load team members:', err);
      setError(t('team.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

  }, []);

  const dateLocale = language === 'fr' ? 'fr-CH' : 'de-CH';

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString(dateLocale);
    } catch {
      return '-';
    }
  };

  const adminCount = members.filter(m => m.role === 'admin').length;

  const handleRoleChange = async (memberId, newRole) => {
    setActionError('');
    setActionLoading(memberId);
    try {
      await axios.put(`${API_URL}/api/companies/me/members/${memberId}/role`, { role: newRole });
      await fetchData();
    } catch (err) {
      logger.error('Failed to change role:', err);
      setActionError(err.response?.data?.detail || t('team.errorGeneric'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmRemove = async () => {
    const isSelf = removeTarget?.id === user?.id;
    await axios.delete(`${API_URL}/api/companies/me/members/${removeTarget.id}`);
    if (isSelf) {
      await logout();
      navigate('/auth');
    } else {
      await fetchData();
    }
  };

  const actionBtnBase = 'inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  // RESP-3a — boutons d'action factorisés : rendus à l'identique dans la table
  // (>= lg) et dans les cartes (< lg). Source unique, pour que les deux vues ne
  // puissent pas diverger. `idSuffix` distingue les data-testid des cartes :
  // les deux vues coexistent dans le DOM (l'une masquée en CSS), et des testid
  // dupliqués rendraient tout getByTestId ambigu en mode strict Playwright.
  const renderMemberActions = (m, idSuffix = '') => {
    const isCurrent = m.id === user?.id;
    const isLastAdminOnRow = m.role === 'admin' && adminCount <= 1;
    const rowLoading = actionLoading === m.id;

    return (
      <>
        {/* Role toggle — only for others, not on last admin */}
        {!isCurrent && (
          <button
            data-testid={`toggle-role-${m.id}${idSuffix}`}
            onClick={() => handleRoleChange(m.id, m.role === 'admin' ? 'member' : 'admin')}
            disabled={rowLoading || isLastAdminOnRow}
            title={isLastAdminOnRow ? t('team.errorRemoveLastAdmin') : ''}
            className={`${actionBtnBase} ${
              m.role === 'admin'
                ? 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20'
                : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
            }`}
          >
            {m.role === 'admin' ? (
              <>
                <ShieldOff className="w-4 h-4" />
                {t('team.demote')}
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                {t('team.promote')}
              </>
            )}
          </button>
        )}

        {/* Remove / Leave */}
        <button
          data-testid={isCurrent ? `leave-team-btn${idSuffix}` : `remove-${m.id}${idSuffix}`}
          onClick={() => setRemoveTarget(m)}
          disabled={rowLoading || isLastAdminOnRow}
          title={isLastAdminOnRow ? t('team.errorRemoveLastAdmin') : ''}
          className={`${actionBtnBase} bg-red-500/10 text-red-500 hover:bg-red-500/20`}
        >
          {isCurrent ? <LogOut className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
          {isCurrent ? t('team.leaveTeam') : t('team.removeMember')}
        </button>
      </>
    );
  };

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t('team.title')}</h1>
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              {t('team.subtitle').replace('{company}', companyName || '...')}
            </p>
          </div>
        </div>

        <button
          onClick={() => setInviteOpen(true)}
          data-testid="invite-button"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
            isDark
              ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          <UserPlus className="w-5 h-5" />
          {t('team.inviteButton')}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      )}

      {/* Error (initial fetch) */}
      {!loading && error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Action error (banner above table) */}
      {!loading && !error && actionError && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {actionError}
        </div>
      )}

      {/* Members table — vue >= lg, inchangée. On masque en max-lg: plutôt que
          d'afficher via une variante lg: du display table : cette variante-là
          n'existe pas au bundle (vérifié), et l'émettre aurait ajouté une règle
          >= lg, cassant l'invariant. En max-lg: la classe de base reste
          intacte, et le rendu desktop avec elle.
          ⚠️ Ne PAS citer ici le nom exact de cette variante : le scanner
          Tailwind lit aussi les commentaires et la régénérerait. */}
      {!loading && !error && (
        <div className={`rounded-xl border overflow-hidden max-lg:hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <table className="w-full">
            <thead>
              <tr className={isDark ? 'bg-slate-700' : 'bg-gray-50'}>
                <th className="text-left px-4 py-3 font-medium">{t('team.member')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('team.email')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('team.role')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('team.addedOn')}</th>
                <th className="text-right px-4 py-3 font-medium">{t('team.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const isCurrent = m.id === user?.id;
                const initial = (m.name || m.email || '?')[0]?.toUpperCase();

                return (
                  <tr
                    key={m.id}
                    data-testid={`team-row-${m.id}`}
                    className={`border-t ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                          {initial}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{m.name || '-'}</span>
                          {isCurrent && (
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              ({t('team.youBadge')})
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{m.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        m.role === 'admin'
                          ? 'bg-amber-500/20 text-amber-500'
                          : isDark ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {m.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDate(m.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      {renderMemberActions(m)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Members cards — vue < lg (CIBLE VISUELLE décision 4 : cartes empilées
          pour Team seul). Répare une FONCTION PERDUE : le wrapper de la table
          est en overflow-hidden, donc à 390px la colonne actions n'était pas
          seulement illisible, elle était rognée et inatteignable.
          `hidden` en base (classe déjà au bundle) => aucune règle >= lg
          ajoutée, et display:none n'occupe aucun espace sur desktop. */}
      {!loading && !error && (
        <div className="hidden max-lg:block space-y-3" data-testid="team-cards">
          {members.map(m => {
            const isCurrent = m.id === user?.id;
            const initial = (m.name || m.email || '?')[0]?.toUpperCase();

            return (
              <div
                key={m.id}
                data-testid={`team-card-${m.id}`}
                className={`rounded-xl border p-4 space-y-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
              >
                {/* Titre : membre */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {initial}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{m.name || '-'}</span>
                    {isCurrent && (
                      <span className={`text-xs flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        ({t('team.youBadge')})
                      </span>
                    )}
                  </div>
                </div>

                {/* Paires label / valeur. L'email peut être long : on le laisse
                    se couper plutôt que le tronquer, pour qu'il reste lisible
                    en entier. break-words est déjà au bundle — préféré à une
                    coupe plus agressive, qui aurait ajouté une règle >= lg. */}
                <dl className="text-sm space-y-1.5">
                  <div className="flex gap-2">
                    <dt className={`flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('team.email')}</dt>
                    <dd className={`break-words ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{m.email}</dd>
                  </div>
                  <div className="flex gap-2 items-center">
                    <dt className={`flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('team.role')}</dt>
                    <dd>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        m.role === 'admin'
                          ? 'bg-amber-500/20 text-amber-500'
                          : isDark ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {m.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')}
                      </span>
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className={`flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('team.addedOn')}</dt>
                    <dd className={isDark ? 'text-slate-400' : 'text-gray-500'}>{formatDate(m.created_at)}</dd>
                  </div>
                </dl>

                {/* Pied : actions. flex-wrap car deux boutons libellés ne
                    tiennent pas sur une ligne de 358px. */}
                <div className={`flex flex-wrap gap-2 pt-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
                  {renderMemberActions(m, '-card')}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <InviteModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={fetchData}
        defaultLanguage={user?.language || language}
      />

      <ConfirmRemoveModal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        member={removeTarget}
        isSelf={removeTarget?.id === user?.id}
        companyName={companyName}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default TeamPage;
